import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	IconButton,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Modal,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import { debounce } from "lodash";
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useBus, useListener } from "react-bus";
import { AddUserModalProps } from "./types";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AppContext, IAppContext } from "../../../../Contexts/AppContext";
import UserApi from "../../../../api/user";
import { UserRole, UserStatus } from "../../../../models/User";
import PermissionaryApi from "../../../../api/permissionary";
import { Permissionary } from "../../../../models/Permissionary";
import {
	getAdminPermissionaryIds,
	getUserPermissionariesIds,
} from "../../../../utils/userPermissionaries";

const style = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	border: "2px solid #000",
	boxShadow: 24,
	p: 4,
};

export default function AddUserModal(props: AddUserModalProps) {
	const [open, setOpen] = useState(false);
	const [user, setUser] = useState({
		email: "",
		password: "",
		role: UserRole.USER,
		permissionariesIds: [] as string[],
		permissionaryRoles: [] as { permissionaryId: string; role: UserRole }[],
	});
	const [permissionaries, setPermissionaries] = useState<Permissionary[]>([]);
	const [loading, setLoading] = useState(false);
	const [checkingEmail, setCheckingEmail] = useState(false);
	const [existingUserExists, setExistingUserExists] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const emailLookupRequestIdRef = useRef(0);
	const bus = useBus();

	const { userContext, setSnackBar, snackBar } = useContext(
		AppContext
	) as IAppContext;

	const handleOpen = useCallback(() => {
		const adminPermissionaryIds = getAdminPermissionaryIds(userContext);
		const availablePermissionaryIds =
			adminPermissionaryIds.length > 0
				? adminPermissionaryIds
				: getUserPermissionariesIds(userContext);
		const selectedPermissionaryIds =
			userContext?.permissionaryId &&
			availablePermissionaryIds.includes(userContext.permissionaryId)
				? [userContext.permissionaryId]
				: availablePermissionaryIds;
		emailLookupRequestIdRef.current += 1;
		setCheckingEmail(false);
		setExistingUserExists(false);
		setShowPassword(false);
		setUser({
			email: "",
			password: "",
			role: UserRole.USER,
			permissionariesIds: selectedPermissionaryIds,
			permissionaryRoles: selectedPermissionaryIds.map((permissionaryId) => ({
				permissionaryId,
				role: UserRole.USER,
			})),
		});
		setOpen(true);
	}, [userContext]);
	const handleClose = () => {
		emailLookupRequestIdRef.current += 1;
		setOpen(false);
		setCheckingEmail(false);
		setExistingUserExists(false);
	};

	const getPermissionaryId = useCallback((permissionary: Permissionary) => {
		return permissionary.id || permissionary._id;
	}, []);

	const getPermissionaryName = useCallback(
		(permissionaryId: string) => {
			return (
				permissionaries.find(
					(permissionary) => getPermissionaryId(permissionary) === permissionaryId
				)?.name || permissionaryId
			);
		},
		[getPermissionaryId, permissionaries]
	);

	const fetchPermissionaries = useCallback(async () => {
		if (!userContext?.userId) {
			setPermissionaries([]);
			return;
		}

		try {
			const response = (await PermissionaryApi.getPermissionariesByUserId({
				userId: userContext.userId,
				token: localStorage.getItem("accessToken"),
			})) as Permissionary[];

			const adminPermissionaryIds = getAdminPermissionaryIds(userContext);
			setPermissionaries(
				Array.isArray(response)
					? response.filter((permissionary) =>
							adminPermissionaryIds.includes(getPermissionaryId(permissionary))
					  )
					: []
			);
		} catch (error) {
			console.error(error);
			setPermissionaries([]);
		}
	}, [getPermissionaryId, userContext]);

	const debouncedCheckUserExistsByEmail = useMemo(
		() =>
			debounce(async (email: string, requestId: number) => {
				if (!email) {
					setCheckingEmail(false);
					setExistingUserExists(false);
					return;
				}

				try {
					const response = await UserApi.userExistsByEmail({
						token: localStorage.getItem("accessToken"),
						email,
					});

					if (requestId !== emailLookupRequestIdRef.current) {
						return;
					}

					setExistingUserExists(response.exists);
					if (response.exists) {
						setShowPassword(false);
						setUser((currentUser) => ({
							...currentUser,
							password: "",
						}));
					}
				} catch (error) {
					if (requestId !== emailLookupRequestIdRef.current) {
						return;
					}

					console.error(error);
					setExistingUserExists(false);
				} finally {
					if (requestId === emailLookupRequestIdRef.current) {
						setCheckingEmail(false);
					}
				}
			}, 500),
		[]
	);

	const create = useCallback(async () => {
		if (user.permissionariesIds.length === 0) {
			return;
		}
			try {
				setLoading(true);
				const accessToken = localStorage.getItem("accessToken");
				const permissionaryRoles = user.permissionariesIds.map(
					(permissionaryId) => ({
						permissionaryId,
						role:
							user.permissionaryRoles.find(
								(permissionaryRole) =>
									permissionaryRole.permissionaryId === permissionaryId
							)?.role || UserRole.USER,
					})
				);

				await UserApi.addUser({
					token: accessToken || "",
					user: {
						email: user.email,
						password: user.password,
						permissionariesIds: user.permissionariesIds,
						permissionaryRoles,
						role: permissionaryRoles[0]?.role || UserRole.USER,
						status: UserStatus.ACTIVE,
					},
				});

			bus.emit("reloadUsersScreenData");
			handleClose();

			setSnackBar({
				...snackBar,
				open: true,
				message: "Usuario creado con exito",
				autoHideDuration: 6000,
				severity: "success",
			});
		} catch (error: any) {
			console.error(error);
			setSnackBar({
				...snackBar,
				open: true,
				message: "Error al crear el usuario",
				autoHideDuration: 6000,
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [bus, snackBar, setSnackBar, user]);

	useEffect(() => {
		if (open) {
			fetchPermissionaries();
		}
	}, [fetchPermissionaries, open]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const email = user.email.trim().toLowerCase();
		const requestId = emailLookupRequestIdRef.current + 1;
		emailLookupRequestIdRef.current = requestId;

		setCheckingEmail(Boolean(email));
		setExistingUserExists(false);
		debouncedCheckUserExistsByEmail(email, requestId);

		return () => {
			debouncedCheckUserExistsByEmail.cancel();
		};
	}, [debouncedCheckUserExistsByEmail, open, user.email]);

	useListener("openAddUserModal", handleOpen);
	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby='modal-modal-title'
			aria-describedby='modal-modal-description'>
			<Box sx={style}>
				<Typography
					textAlign={"center"}
					id='modal-modal-title'
					variant='h6'
					component='h2'>
					Agregar Usuario
				</Typography>
				<div style={{ marginBottom: "15px", width: "100%" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel htmlFor='outlined-adornment-username'>Email</InputLabel>
						<OutlinedInput
							id='outlined-adornment-username'
							type={"text"}
							placeholder='Email'
							label='Email'
							value={user.email}
							onChange={(event) => {
								setUser({
									...user,
									email: event.target.value.toLowerCase(),
								});
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									create();
								}
							}}
						/>
					</FormControl>
				</div>
				<div style={{ marginBottom: "15px", width: "100%" }}>
					<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
						<InputLabel id='permissionaries-select-label'>
							Permisionarios
						</InputLabel>
						<Select
							labelId='permissionaries-select-label'
							id='permissionaries-select'
							multiple
							value={user.permissionariesIds}
							input={<OutlinedInput label='Permisionarios' />}
							renderValue={(selected) => (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
									{selected.map((permissionaryId) => (
										<Chip
											key={permissionaryId}
											label={getPermissionaryName(permissionaryId)}
											size='small'
										/>
									))}
								</Box>
							)}
								onChange={(event: SelectChangeEvent<string[]>) => {
									const value = event.target.value;
									const permissionariesIds =
										typeof value === "string" ? value.split(",") : value;
									setUser({
										...user,
										permissionariesIds,
										permissionaryRoles: permissionariesIds.map(
											(permissionaryId) => ({
												permissionaryId,
												role:
													user.permissionaryRoles.find(
														(permissionaryRole) =>
															permissionaryRole.permissionaryId ===
															permissionaryId
													)?.role || UserRole.USER,
											})
										),
									});
								}}>
							{permissionaries.map((permissionary) => {
								const permissionaryId = getPermissionaryId(permissionary);

								return (
									<MenuItem key={permissionaryId} value={permissionaryId}>
										<Checkbox
											checked={user.permissionariesIds.includes(permissionaryId)}
										/>
										<ListItemText primary={permissionary.name} />
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</div>
				{!existingUserExists && (
					<div style={{ marginBottom: "15px", width: "100%" }}>
						<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
							<InputLabel htmlFor='outlined-adornment-password'>
								Contraseña
							</InputLabel>
							<OutlinedInput
								id='outlined-adornment-password'
								type={showPassword ? "text" : "password"}
								placeholder='Contraseña'
								value={user.password}
								endAdornment={
									<InputAdornment position='end'>
										<IconButton
											aria-label='toggle password visibility'
											onClick={() => setShowPassword(!showPassword)}
											edge='end'>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								}
								label='Contraseña'
								onChange={(event) => {
									setUser({ ...user, password: event.target.value });
								}}
								onKeyUp={(event) => {
									if (event.key === "Enter") {
										create();
									}
								}}
							/>
						</FormControl>
					</div>
				)}
				{user.permissionariesIds.map((permissionaryId) => (
					<div
						key={permissionaryId}
						style={{ marginBottom: "15px", width: "100%" }}>
						<FormControl sx={{ m: 1, width: "90%" }} variant='outlined'>
							<InputLabel id={`role-select-label-${permissionaryId}`}>
								Rol en {getPermissionaryName(permissionaryId)}
							</InputLabel>
							<Select
								labelId={`role-select-label-${permissionaryId}`}
								id={`role-select-${permissionaryId}`}
								value={
									user.permissionaryRoles.find(
										(permissionaryRole) =>
											permissionaryRole.permissionaryId === permissionaryId
									)?.role || UserRole.USER
								}
								label={`Rol en ${getPermissionaryName(permissionaryId)}`}
								onChange={(e: SelectChangeEvent) => {
									setUser({
										...user,
										permissionaryRoles: user.permissionariesIds.map(
											(currentPermissionaryId) => ({
												permissionaryId: currentPermissionaryId,
												role:
													currentPermissionaryId === permissionaryId
														? (e.target.value as UserRole)
														: user.permissionaryRoles.find(
																(permissionaryRole) =>
																	permissionaryRole.permissionaryId ===
																	currentPermissionaryId
														  )?.role || UserRole.USER,
											})
										),
									});
								}}>
								<MenuItem value={UserRole.ADMIN}>Administrador</MenuItem>
								<MenuItem value={UserRole.USER}>Usuario</MenuItem>
							</Select>
						</FormControl>
					</div>
				))}
				<div style={{ marginTop: "10px", textAlign: "center" }}>
					{loading ? (
						<CircularProgress size={30} />
					) : (
						<Button
							disabled={
								user.email === "" ||
								(!existingUserExists && user.password === "") ||
								checkingEmail ||
								user.permissionariesIds.length === 0
							}
							variant='contained'
							color='primary'
							sx={{
								width: "40%",
								marginTop: 2,
							}}
							onClick={() => create()}>
							Crear
						</Button>
					)}
				</div>
			</Box>
		</Modal>
	);
}
